import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { products } from '../../../mocks/products';
import ProductModal from './ProductModal';
import { useCart } from '../../../context/CartContext';

interface MerchandiseProps {
  onCartChange?: (count: number) => void;
}

export default function Merchandise({ onCartChange }: MerchandiseProps) {
  const navigate = useNavigate();
  const { cartItems, addToCart, removeFromCart, cartTotal, cartCount } = useCart();
  const [activeCategory, setActiveCategory] = useState('ALL');
  const [selectedVariants, setSelectedVariants] = useState<Record<number, number>>({});
  const [showCart, setShowCart] = useState(false);
  const [addedId, setAddedId] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<typeof products[0] | null>(null);

  useEffect(() => {
    onCartChange?.(cartCount);
  }, [cartCount, onCartChange]);

  const categories = ['ALL', 'HOODIES', 'TEES', 'CAPS', 'ACCESSORIES'];

  const filteredProducts =
    activeCategory === 'ALL'
      ? products
      : products.filter((p) => p.category === activeCategory);

  const getActiveImage = (product: typeof products[0]) => {
    if (product.variants && product.variants.length > 0) {
      const idx = selectedVariants[product.id] ?? 0;
      return product.variants[idx].image;
    }
    return product.image;
  };

  const handleAddToCart = (product: typeof products[0], variantIdx?: number) => {
    const resolvedIdx = variantIdx ?? (selectedVariants[product.id] ?? 0);
    const variant = product.variants?.[resolvedIdx];
    addToCart({
      productId: product.id,
      name: product.name,
      price: product.price,
      variant: variant?.label,
      image: variant?.image ?? product.image,
    });
    const uniqueKey = `${product.id}-${variant?.id ?? 'default'}`;
    setAddedId(uniqueKey);
    setTimeout(() => setAddedId(null), 1200);
  };

  const handleRemoveFromCart = (index: number) => {
    removeFromCart(index);
  };

  return (
    <section id="merchandise" className="bg-black py-24 px-6 relative overflow-hidden">
      {/* Grid Pattern Overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            'linear-gradient(rgba(139, 0, 0, 0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(139, 0, 0, 0.2) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
        }}
      ></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-12">
          <div>
            <h2
              className="text-6xl font-bold mb-2 inline-block relative"
              style={{ color: '#8B0000' }}
            >
              NIGHTMARE COLLECTION
              <span
                className="absolute bottom-0 left-0 h-1 rounded-full"
                style={{ backgroundColor: '#8B0000', width: '50%' }}
              ></span>
            </h2>
            <p className="text-2xl text-gray-500">Official Merchandise</p>
          </div>
          <button
            onClick={() => setShowCart(true)}
            className="mt-6 md:mt-0 border-2 border-gray-600 text-gray-400 hover:border-[#8B0000] hover:text-[#8B0000] font-semibold px-8 py-3 rounded transition-all duration-300 whitespace-nowrap cursor-pointer relative"
          >
            <i className="ri-shopping-bag-line mr-2"></i>
            VIEW CART
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center bg-[#8B0000] text-white text-xs font-bold rounded-full">
                {cartCount}
              </span>
            )}
          </button>
        </div>

        {/* Category Filter */}
        <div className="flex items-center space-x-3 mb-12 overflow-x-auto pb-4">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-2 rounded font-semibold text-sm uppercase tracking-wider transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeCategory === category
                  ? 'bg-[#8B0000] text-white'
                  : 'bg-[#1a1a1a] text-gray-400 hover:bg-[#252525] border border-gray-800'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
          {filteredProducts.map((product) => {
            const currentVariantIdx = selectedVariants[product.id] ?? 0;
            const currentVariant = product.variants?.[currentVariantIdx];
            const uniqueKey = `${product.id}-${currentVariant?.id ?? 'default'}`;

            return (
              <div
                key={product.id}
                onClick={() => setSelectedProduct(product)}
                className="group bg-[#0d0d0d] rounded-lg overflow-hidden hover:transform hover:-translate-y-2 transition-all duration-300 border border-gray-800 hover:border-gray-700 cursor-pointer"
              >
                {/* Product Image */}
                <div className="relative h-80 w-full overflow-hidden flex items-center justify-center" style={{ background: '#6b7280' }}>
                  {product.variants && product.variants.length > 1 && (
                    <>
                      <div className="absolute top-2 right-2 w-[85%] h-[90%] rounded-md overflow-hidden opacity-20 rotate-3 z-0">
                        <img
                          src={product.variants[(currentVariantIdx + 2) % product.variants.length].image}
                          alt="variant preview"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="absolute top-1 right-1 w-[90%] h-[93%] rounded-md overflow-hidden opacity-35 rotate-[1.5deg] z-[1]">
                        <img
                          src={product.variants[(currentVariantIdx + 1) % product.variants.length].image}
                          alt="variant preview"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </>
                  )}
                  <img
                    src={getActiveImage(product)}
                    alt={product.name}
                    title={`${product.name} - Nightmare Baseball Merchandise`}
                    className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform duration-500 relative z-[2]"
                  />
                  {product.badge && (
                    <div className="absolute top-3 left-3 z-[3]">
                      <span className="bg-[#8B0000] text-white text-xs font-bold uppercase px-3 py-1 rounded-sm tracking-wider shadow-lg">
                        {product.badge}
                      </span>
                    </div>
                  )}
                  <div className="absolute inset-0 z-[4] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="bg-black/70 text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full border border-gray-600">
                      <i className="ri-eye-line mr-1"></i> Quick View
                    </span>
                  </div>
                </div>

                {/* Product Info */}
                <div className="p-6">
                  <h3 className="text-white text-xl font-semibold mb-2">{product.name}</h3>
                  <p className="text-gray-400 text-lg font-bold mb-3">
                    $
                    {typeof product.price === 'number' && product.price % 1 !== 0
                      ? product.price.toFixed(2)
                      : product.price}
                  </p>

                  {product.variants && product.variants.length > 1 && (
                    <div className="flex items-center gap-2 mb-4" onClick={(e) => e.stopPropagation()}>
                      {product.variants.map((variant, idx) => (
                        <button
                          key={variant.id}
                          onClick={() =>
                            setSelectedVariants((prev) => ({ ...prev, [product.id]: idx }))
                          }
                          className={`w-7 h-7 rounded-full border-2 transition-all duration-200 cursor-pointer flex items-center justify-center ${
                            currentVariantIdx === idx
                              ? 'border-white scale-110'
                              : 'border-gray-600 hover:border-gray-400'
                          }`}
                          style={{ backgroundColor: variant.color }}
                          title={variant.label}
                        >
                          {currentVariantIdx === idx && (
                            <i className="ri-check-line text-white text-xs"></i>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={(e) => { e.stopPropagation(); handleAddToCart(product); }}
                    className={`w-full font-semibold py-3 rounded transition-all duration-300 opacity-0 group-hover:opacity-100 whitespace-nowrap cursor-pointer ${
                      addedId === uniqueKey
                        ? 'bg-green-700 text-white'
                        : 'bg-[#8B0000] hover:bg-[#6B0000] text-white'
                    }`}
                  >
                    {addedId === uniqueKey ? (
                      <>
                        <i className="ri-check-line mr-1"></i> ADDED!
                      </>
                    ) : (
                      'ADD TO CART'
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Product Detail Modal */}
      <ProductModal
        product={selectedProduct}
        onClose={() => setSelectedProduct(null)}
        onAddToCart={(product, variantIdx) => handleAddToCart(product, variantIdx)}
      />

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowCart(false)}></div>
          <div className="relative w-full max-w-md bg-[#111] h-full overflow-y-auto shadow-2xl border-l border-gray-800 animate-slide-in">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-white text-2xl font-bold">Your Cart</h3>
                <button
                  onClick={() => setShowCart(false)}
                  className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {cartItems.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shopping-bag-line text-4xl text-gray-600"></i>
                  </div>
                  <p className="text-gray-500 text-lg">Your cart is empty</p>
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-8">
                    {cartItems.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-4 bg-[#1a1a1a] rounded-lg p-4 border border-gray-800"
                      >
                        <div className="w-16 h-16 rounded-md overflow-hidden bg-[#0a0a0a] flex-shrink-0">
                          <img src={item.image} alt={item.name} className="w-full h-full object-contain" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-semibold text-sm truncate">{item.name}</p>
                          {item.variant && (
                            <p className="text-gray-500 text-xs mt-0.5">{item.variant}</p>
                          )}
                          <p className="text-gray-400 font-bold text-sm mt-1">
                            $
                            {typeof item.price === 'number' && item.price % 1 !== 0
                              ? item.price.toFixed(2)
                              : item.price}
                            {item.quantity > 1 && (
                              <span className="text-gray-600 font-normal ml-1">x{item.quantity}</span>
                            )}
                          </p>
                        </div>
                        <button
                          onClick={() => removeFromCart(index)}
                          className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-red-500 transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-lg"></i>
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-800 pt-6">
                    <div className="flex items-center justify-between mb-6">
                      <span className="text-gray-400 text-lg">Total</span>
                      <span className="text-white text-2xl font-bold">
                        ${cartTotal % 1 !== 0 ? cartTotal.toFixed(2) : cartTotal}
                      </span>
                    </div>
                    <button
                      onClick={() => { setShowCart(false); navigate('/checkout'); }}
                      className="w-full bg-[#8B0000] hover:bg-[#6B0000] text-white font-bold py-4 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-lg"
                    >
                      <i className="ri-secure-payment-line mr-2"></i>CHECKOUT
                    </button>
                    <button
                      onClick={() => setShowCart(false)}
                      className="w-full mt-3 border border-gray-700 hover:border-gray-500 text-gray-400 hover:text-white font-semibold py-3 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
                    >
                      <i className="ri-arrow-left-line mr-1"></i>Continue Shopping
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes slideIn {
              from { transform: translateX(100%); }
              to { transform: translateX(0); }
            }
            .animate-slide-in {
              animation: slideIn 0.3s ease-out forwards;
            }
          `,
        }}
      />
    </section>
  );
}
